import LaunchIcon from "@mui/icons-material/Launch";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "components/Loader";
import { CLIENT_MODULE_DOCUMENTS_QUERY_KEY, fetchModuleDocuments } from "./clientDocuments.api";
import { filterModuleGroups, moduleEntryPath, moduleLabel } from "./clientDocuments.utils";

const formatDate = (value) => {
  if (!value) return null;
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return null;
  }
};

/**
 * Read-only mirror of documents that live in the business modules. Nothing here can be edited or
 * deleted from this tab: these files belong to their module, and the tab only surfaces them so
 * staff can see everything a client has in one place.
 */
const ModuleDocumentsPanel = ({ clientId }) => {
  const [search, setSearch] = useState("");
  const [moduleType, setModuleType] = useState("");

  // refetchOnMount must stay "always": the QueryClient default is refetchOnMount:false.
  const query = useQuery({
    queryKey: [CLIENT_MODULE_DOCUMENTS_QUERY_KEY, clientId],
    queryFn: () => fetchModuleDocuments(clientId),
    enabled: Boolean(clientId),
    refetchOnMount: "always",
  });

  const groups = useMemo(() => query.data || [], [query.data]);
  const visible = useMemo(
    () => filterModuleGroups(groups, { search, moduleType }),
    [groups, search, moduleType]
  );

  if (query.isLoading) return <Loader />;
  if (query.error) return <Alert severity="error">{query.error.message}</Alert>;

  if (groups.length === 0) {
    return (
      <Alert severity="info">
        No module documents for this client, or you do not have access to the modules holding them.
      </Alert>
    );
  }

  return (
    <Box className="space-y-3">
      <Box className="flex flex-wrap gap-2">
        <TextField
          size="small"
          label="Search documents"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="min-w-[220px]"
        />
        <TextField
          select
          size="small"
          label="Module"
          value={moduleType}
          onChange={(event) => setModuleType(event.target.value)}
          className="min-w-[180px]"
        >
          <MenuItem value="">All modules</MenuItem>
          {groups.map((group) => (
            <MenuItem key={group.moduleType} value={group.moduleType}>
              {moduleLabel(group.moduleType)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {visible.length === 0 && <Alert severity="info">Nothing matches that search.</Alert>}

      {visible.map((group) => (
        <Accordion key={group.moduleType} defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              {moduleLabel(group.moduleType)} ({group.entries.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="space-y-3">
            {group.entries.map((entry) => {
              const path = moduleEntryPath(group.moduleType, entry.entryId);
              return (
                <Box key={entry.entryId} className="border border-app-border rounded-lg p-3">
                  <Box className="flex items-center justify-between gap-2">
                    <Typography variant="body2" className="font-medium">
                      {entry.entryLabel}
                    </Typography>
                    {path && (
                      <Tooltip title="Open in module">
                        <IconButton size="small" component={Link} to={path}>
                          <LaunchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {entry.documents.map((document, index) => {
                    const added = formatDate(document.addedAt);
                    return (
                      <Box
                        key={`${document.url}-${index}`}
                        className="flex items-center justify-between gap-2 mt-2"
                      >
                        <Box className="min-w-0">
                          <Typography variant="body2" noWrap>
                            {document.name || "Untitled"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[document.addedBy, added].filter(Boolean).join(" - ") || "No details"}
                          </Typography>
                        </Box>
                        <Box className="flex items-center gap-1 shrink-0">
                          <Chip size="small" variant="outlined" label="From module" />
                          {/* One action, not a preview/download pair: these are presigned S3
                              URLs on another origin, where the download attribute is ignored and
                              a Download button would silently behave like Open. */}
                          <Tooltip title="Open file">
                            <IconButton
                              size="small"
                              href={document.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ModuleDocumentsPanel;
